import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SiteId, TaskId } from '@interfaces/IScheduler';
import { useSiteLocations } from '@hooks/useSiteLocations';
import { useSchedulerActions, useSchedulerSite, useSchedulerStale, useSchedulerTask } from '../../hooks/useSchedulerStore';
import { useTaskLadderOrPlanned } from '../../hooks/useTaskLadderOrPlanned';
import { useTaskPlanAutoSave } from '../../hooks/useTaskPlanAutoSave';
import { canSplitByLocations } from '../../lib/splitTaskByLocations';
import { freezeOf, isFrozen } from '../../lib/freeze';
import { AreaGridPicker } from '../CommitReview/AreaGridPicker';
import { SplitByLocationModal } from '../SplitByLocationModal/SplitByLocationModal';
import styles from './TaskAreaPicker.module.css';

// ---------------------------------------------------------------------------
// TaskAreaPicker — which areas the task covers, edited in the promise block.
//
// The map is the commit review's `AreaGridPicker` (one row per floor, one cell
// per zone, the floor button taking or dropping the whole floor): the review
// and the drawer ask the same question, so they ask it with the same control.
// This wrapper is what makes it a PLAN edit rather than a review draft — each
// change dispatches the add/remove diff to the store and saves itself through
// the per-task plan write (2026-09-23 amendment); a task not saved yet rides
// the toolbar Save with the rest of it. Frozen while the schedule is STALE
// (behind upstream), like every other write.
//
// Per-area STATUS is not edited here — that is the progress board's job, above.
// ---------------------------------------------------------------------------

interface ITaskAreaPickerProps {
  siteId: SiteId;
  taskId: TaskId;
  /** Loaded schedule number — enables the auto-save (absent in Router-free tests). */
  scheduleNumber?: string;
}

export function TaskAreaPicker({ siteId, taskId, scheduleNumber }: ITaskAreaPickerProps) {
  const { t } = useTranslation();
  const site = useSchedulerSite(siteId);
  const task = useSchedulerTask(siteId, taskId);
  const stale = useSchedulerStale(siteId);
  const catalog = useSiteLocations(siteId);
  const { taskLocationsBulkToggled, markDirty } = useSchedulerActions();
  const autoSave = useTaskPlanAutoSave(siteId, scheduleNumber, taskId);
  const [splitOpen, setSplitOpen] = useState(false);
  // WHERE stops being a question the moment the group goes live upstream: the
  // areas are already Trusstor area sequence items with their own status
  // (2026-09-20 amendment, decision 2). The map stays, inert.
  const frozen = isFrozen(freezeOf(useTaskLadderOrPlanned(task, taskId).rung), 'areas');
  if (!task) return null;

  // Administrative work has no areas (2026-09-18 commit UX design): they exist
  // only to become Trusstor area sequence items, and there is no Trusstor side.
  if (task.kind === 'administrative') {
    return <p className={styles.note}>{t('scheduler.panel.locations.administrative')}</p>;
  }

  const assigned = (task.locations ?? []).map((location) => location.locationId);
  const canSplit = canSplitByLocations(task, site?.tasks ?? [], frozen);

  const apply = (next: readonly string[]) => {
    if (stale) return; // frozen while behind upstream — no dispatch
    const before = new Set(assigned);
    const after = new Set(next);
    const add = next.filter((id) => !before.has(id));
    const remove = assigned.filter((id) => !after.has(id));
    if (add.length === 0 && remove.length === 0) return;
    taskLocationsBulkToggled({ siteId, taskId, add, remove });
    if (autoSave.autoSaves()) autoSave.save({ areaIds: [...next] });
    else markDirty({ siteId });
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.toolbar}>
        <p className={styles.summary}>
          {assigned.length === 0
            ? t('scheduler.panel.locations.empty')
            : t('scheduler.panel.locations.assignedCount', { total: assigned.length })}
        </p>
        {canSplit && (
          <button type="button" className={styles.splitBtn} onClick={() => setSplitOpen(true)}>
            {t('scheduler.panel.locations.splitButton')}
          </button>
        )}
      </div>

      {splitOpen && (
        <SplitByLocationModal open onClose={() => setSplitOpen(false)} siteId={siteId} taskId={taskId} />
      )}

      <AreaGridPicker floors={catalog.floors} value={assigned} onChange={apply} readOnly={frozen} />
      {frozen && <p className={styles.note}>{t('scheduler.freeze.scope')}</p>}
    </div>
  );
}
